import { CellClassParams, ColDef, ExcelStyle } from 'ag-grid-community';
import {
  ICustomNumberFormatConfig,
  IFormatterDataModel,
  StandardFormatterId,
} from '@foresightdata/web-common-core';
import { FMT } from '@foresightdata/web-common-formatter-service';
import { getCurrencySymbolFor } from './currency-util';
import { isObject } from './object-util';

export const ExcelFormatterIdPrefix = 'excel_';

interface CompactTier {
  /** One trailing comma per factor of 1000 Excel should scale the displayed value down by. */
  scale: string;
  threshold: number;
  unit: string;
}

const ThousandsTier: CompactTier = { scale: ',', threshold: 1e3, unit: 'K' };
const MillionsTier: CompactTier = { scale: ',,', threshold: 1e6, unit: 'M' };
const BillionsTier: CompactTier = { scale: ',,,', threshold: 1e9, unit: 'B' };
const TrillionsTier: CompactTier = { scale: ',,,,', threshold: 1e12, unit: 'T' };

const CompactTiers: CompactTier[] = [ThousandsTier, MillionsTier, BillionsTier, TrillionsTier];

const CompactNegativeCondition = '[<0]';
const CompactVariantSeparator = '__';
const PercentSuffix = '%';

export function getExcelAgFormat(model: IFormatterDataModel<unknown>): ExcelStyle | null {
  const id = getExcelFormatterId(model.id as StandardFormatterId);

  if (id === null) {
    return null;
  }

  switch (model.type) {
    case 'number':
      return {
        dataType: 'Number',
        id,
        numberFormat: {
          format: numberFormatConfigToExcelString(model.config as ICustomNumberFormatConfig),
        },
      };
    case 'date':
      return {
        dataType: 'DateTime',
        id,
        numberFormat: { format: 'mm/dd/yyyy;;;' },
      };
    default:
      return null;
  }
}

/**
 * Convert from ICustomNumberFormatConfig to Excel number format string as per:
 * - https://www.ablebits.com/office-addins-blog/custom-excel-number-format/
 * - https://support.microsoft.com/en-us/office/number-format-codes-in-excel-for-mac-5026bbd6-04bc-48cd-bf33-80f18b4eae68
 * @param options {ICustomNumberFormatConfig}
 */
export function numberFormatConfigToExcelString(options: ICustomNumberFormatConfig = {}): string {
  if (isCompactNotation(options)) {
    return compactNumberFormatToExcelString(options);
  }

  let formatString = '';

  const positiveFormat = getPositiveFormatNumberForExcel(options);

  formatString += `${positiveFormat};`;
  if (options.negativeValue) {
    formatString += `"${options.negativeValue}"`;
  } else {
    formatString += `-${positiveFormat}`;
  }

  formatString += ';';
  if (options.zeroValue) {
    formatString += `"${options.zeroValue}"`;
  }

  return formatString;
}

export function getExcelFormatterId(id: StandardFormatterId) {
  const formatterModel = FMT.get().getFormatterModel(id);

  if (
    formatterModel &&
    (formatterModel.type === 'number' || formatterModel.type === 'currency' || formatterModel.type === 'date')
  ) {
    return `${ExcelFormatterIdPrefix}${id}`;
  }

  return null;
}

/**
 * The divisor for a column's exported value, resolved from whichever `excel_*` style id its colDef
 * carries. Mirrors the sources the export's own style-class check reads — an array `cellClass` and
 * `cellClassRules` keys — since only those columns export a raw number in the first place.
 */
export function getExcelDivisorForColDef({ cellClass, cellClassRules }: ColDef): number {
  const styleIds = [...(Array.isArray(cellClass) ? cellClass : []), ...Object.keys(cellClassRules ?? {})];

  for (const styleId of styleIds) {
    const model = getFormatterModelForExcelStyleId(styleId);

    if (model) {
      return getExcelValueDivisor(model.config as ICustomNumberFormatConfig | undefined);
    }
  }

  return 1;
}

function getFormatterModelForExcelStyleId(styleId: unknown): IFormatterDataModel<unknown> | undefined {
  if (typeof styleId !== 'string' || !styleId.startsWith(ExcelFormatterIdPrefix)) {
    return undefined;
  }

  // Per-cell magnitude styles append a `__<unit><decimals>` variant, which is not part of the id.
  const formatterId = styleId.slice(ExcelFormatterIdPrefix.length).split(CompactVariantSeparator)[0];

  return FMT.get().getFormatterModel(formatterId as StandardFormatterId);
}

/**
 * Fallback for cells with no per-cell style (see {@link getCompactExcelCellClassRules}). Excel allows
 * only 2 conditions plus a fallback, and one has to catch negatives so signs are never dropped, which
 * leaves room for a single magnitude tier.
 */
function compactNumberFormatToExcelString(options: ICustomNumberFormatConfig): string {
  const plainFormat = getPositiveFormatNumberForExcel(options);

  return [
    `[>=${MillionsTier.threshold}]${getPositiveFormatNumberForExcel(options, MillionsTier)}`,
    // Conditional sections render the absolute value, so the sign has to be written back in.
    `${CompactNegativeCondition}-${plainFormat}`,
    plainFormat,
  ].join(';');
}

/**
 * Whether compact notation can be reproduced in Excel at all. `negativeValue`/`zeroValue` need
 * sections of their own, which the tier conditions already use up. `roundingMode: 'expand'` is there so
 * a displayed value is never below the real one, and Excel has no rounding-mode control, so
 * abbreviating would understate the figure by a whole step ($10k at millions with 2 decimals). Those
 * configs keep exporting the exact full number instead.
 *
 * Percentages are excluded because Excel's `%` token scales the display by 100 while a tier's scaling
 * commas divide by 1000, and no percentage in the product needs a magnitude unit anyway.
 */
function isCompactNotation(options: ICustomNumberFormatConfig): boolean {
  return (
    options.notation === 'compact' &&
    options.roundingMode !== 'expand' &&
    !options.negativeValue &&
    !options.zeroValue &&
    !isPercentConfig(options)
  );
}

/**
 * Whether the config describes a percentage, under either of the two conventions in the registry:
 * Intl's `style: 'percent'` (the raw value is a 0-1 fraction) or a literal `'%'` suffix (the raw value
 * is already on a 0-100 scale). Both export as a real Excel percentage; see {@link getExcelValueDivisor}.
 */
function isPercentConfig(options: ICustomNumberFormatConfig): boolean {
  return options.style === 'percent' || options.suffix === PercentSuffix;
}

/**
 * How much to divide a value by before writing it to Excel. Excel's `%` token multiplies the *display*
 * by 100, so the cell has to hold the fraction — otherwise the number format is cosmetic and arithmetic
 * on the cell is off by a factor of 100. Configs using Intl's `style: 'percent'` are already
 * fractions and need no scaling.
 */
export function getExcelValueDivisor(options: ICustomNumberFormatConfig | undefined): number {
  return options?.suffix === PercentSuffix ? 100 : 1;
}

function getPositiveFormatNumberForExcel(options: ICustomNumberFormatConfig, tier?: CompactTier): string {
  let positiveFormat = '';

  if (options.style === 'currency') {
    positiveFormat = getCurrencySymbolFor(options.currency ?? '') || '';
  }

  if (options.useGrouping !== false) {
    positiveFormat += '#,##0';
  } else {
    positiveFormat += '0';
  }

  positiveFormat += getFractionDigitsForExcel(options, tier);

  // Scaling commas have to follow the digit placeholders directly, ahead of any literal.
  positiveFormat += tier?.scale ?? '';

  if (isPercentConfig(options)) {
    // The bare token, not a quoted literal: it is what makes Excel treat the cell as a percentage.
    return `${positiveFormat}${PercentSuffix}`;
  }

  const literal = `${tier?.unit ?? ''}${options.suffix ?? ''}`;
  if (literal) {
    positiveFormat += `"${literal}"`;
  }

  return positiveFormat === '' ? 'General' : positiveFormat;
}

function getFractionDigitsForExcel(options: ICustomNumberFormatConfig, tier?: CompactTier): string {
  const { maximumFractionDigits: max, minimumFractionDigits: min } = options;

  if (tier) {
    // No Excel placeholder hides the decimal separator when a digit is absent (`0.#` renders 1 as
    // "1."), so the count is fixed. Intl defaults compact to 2 significant digits, for which a
    // single decimal is the closest fixed equivalent.
    const digits = max ?? min ?? 1;
    return digits > 0 ? '.'.padEnd(digits + 1, '0') : '';
  }

  if (min != undefined) {
    return min > 0 ? '.'.padEnd(min + 1, '0') : '';
  }

  if (max != undefined) {
    return max > 0 ? '.'.padEnd(max + 1, '0') : '';
  }

  return '.00';
}

/**
 * One Excel style per magnitude tier and decimal count. Choosing the style from the cell's own value
 * lifts the 2-condition ceiling a single format string has, so K/M/B/T all abbreviate, the format
 * needs no conditions, and Excel handles the sign natively.
 */
export function getCompactExcelStyles(model: IFormatterDataModel<unknown>): ExcelStyle[] {
  const config = model.config as ICustomNumberFormatConfig | undefined;

  if (
    !config ||
    !isCompactNotation(config) ||
    getExcelFormatterId(model.id as StandardFormatterId) === null
  ) {
    return [];
  }

  const decimalRange = getCompactDecimalRange(config);

  return [null, ...CompactTiers].flatMap((tier) =>
    decimalRange.map((decimals) => ({
      dataType: 'Number' as const,
      id: getCompactStyleId(model.id, tier, decimals),
      numberFormat: { format: getCompactFormat(config, tier, decimals) },
    }))
  );
}

/**
 * `cellClassRules` rather than a function `cellClass`, because the export only passes raw numbers
 * through for array-valued cellClass, and rule keys stay static so that check still sees the ids.
 * Exactly one rule matches any value: two matching styles would deep-merge in registration order.
 */
export function getCompactExcelCellClassRules(formatterId: string): ColDef['cellClassRules'] {
  const model = FMT.get().getFormatterModel(formatterId as StandardFormatterId);

  if (!model) {
    return undefined;
  }

  const styles = getCompactExcelStyles(model);

  if (!styles.length) {
    return undefined;
  }

  const rules: Record<string, (params: CellClassParams) => boolean> = {};

  styles.forEach(({ id }) => {
    rules[id] = (params) => getCompactExcelStyleId(model, params.value) === id;
  });

  const baseId = getExcelFormatterId(formatterId as StandardFormatterId);
  if (baseId) {
    // Group totals and blanks have no magnitude to size, so they keep the conditional base format
    // rather than losing the number format altogether.
    rules[baseId] = (params) => getCompactExcelStyleId(model, params.value) === null;
  }

  return rules;
}

// Every rule for a column asks about the same value in turn, so a single-entry memo removes almost
// all of the repeated work while the grid renders.
let lastStyleIdKey = '';
let lastStyleId: string | null = null;

/** The per-cell style id for a value, or null when there is no finite number to size. */
export function getCompactExcelStyleId(model: IFormatterDataModel<unknown>, value: unknown): string | null {
  const config = model.config as ICustomNumberFormatConfig | undefined;

  if (!config || !isCompactNotation(config)) {
    return null;
  }

  const numeric = toFiniteNumber(value);

  if (numeric === null) {
    return null;
  }

  const key = `${model.id}:${numeric}`;
  if (key === lastStyleIdKey) {
    return lastStyleId;
  }

  const { decimals, tier } = selectCompactTier(numeric, config);

  lastStyleIdKey = key;
  lastStyleId = getCompactStyleId(model.id, tier, decimals);

  return lastStyleId;
}

function getCompactStyleId(formatterId: string, tier: CompactTier | null, decimals: number): string {
  return `${ExcelFormatterIdPrefix}${formatterId}${CompactVariantSeparator}${tier?.unit ?? ''}${decimals}`;
}

function getCompactFormat(
  config: ICustomNumberFormatConfig,
  tier: CompactTier | null,
  decimals: number
): string {
  let format = config.style === 'currency' ? getCurrencySymbolFor(config.currency ?? '') || '' : '';

  format += config.useGrouping === false ? '0' : '#,##0';

  if (decimals > 0) {
    format += '.'.padEnd(decimals + 1, '0');
  }

  // Scaling commas have to follow the digit placeholders directly, ahead of any literal.
  format += tier?.scale ?? '';

  const literal = `${tier?.unit ?? ''}${config.suffix ?? ''}`;

  return literal ? `${format}"${literal}"` : format;
}

function getCompactDecimalRange(config: ICustomNumberFormatConfig): number[] {
  // Intl falls back to 2 significant digits, which never renders more than one decimal.
  const max = config.maximumFractionDigits ?? 1;

  // The whole 0..max span is registered rather than starting at minimumFractionDigits: a large
  // magnitude leaves no room for decimals, so a style outside that span would go unregistered and the
  // cell would lose its number format entirely.
  return Array.from({ length: max + 1 }, (_, index) => index);
}

/** How many decimals Intl renders: round at the maximum, then drop trailing zeros to the minimum. */
function getCompactDecimals(mantissa: number, config: ICustomNumberFormatConfig): number {
  const max = config.maximumFractionDigits ?? (mantissa < 10 ? 1 : 0);
  const min = Math.min(config.minimumFractionDigits ?? 0, max);
  const fraction = mantissa.toFixed(max).split('.')[1] ?? '';

  let decimals = fraction.length;
  while (decimals > min && fraction[decimals - 1] === '0') {
    decimals--;
  }

  return decimals;
}

function selectCompactTier(value: number, config: ICustomNumberFormatConfig) {
  const magnitude = Math.abs(value);
  let index = -1;

  while (index + 1 < CompactTiers.length && magnitude >= CompactTiers[index + 1].threshold) {
    index++;
  }

  // Rounding can carry the mantissa to 1000, which Intl shows as the next unit up ($1M, not $1000K).
  while (index + 1 < CompactTiers.length) {
    const mantissa = getMantissa(magnitude, index);
    if (Number(mantissa.toFixed(getCompactDecimals(mantissa, config))) < 1000) {
      break;
    }
    index++;
  }

  const mantissa = getMantissa(magnitude, index);

  return { decimals: getCompactDecimals(mantissa, config), tier: index < 0 ? null : CompactTiers[index] };
}

function getMantissa(magnitude: number, tierIndex: number): number {
  return tierIndex < 0 ? magnitude : magnitude / CompactTiers[tierIndex].threshold;
}

/**
 * Group and total rows arrive as aggregation objects rather than numbers, and the export coerces null
 * to an empty string, so anything without a finite number behind it gets no tier.
 */
function toFiniteNumber(value: unknown): number | null {
  const unwrapped = isObject(value) ? (value as { value?: unknown }).value : value;

  return typeof unwrapped === 'number' && Number.isFinite(unwrapped) ? unwrapped : null;
}
